import React, { useEffect, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { Autocomplete } from "@mui/material";
import { City, Country, State } from "country-state-city";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { addCompany, updateCompany } from "slices/Masters/company/thunk";
import axios from "axios";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { autocompleteDropdownStyles } from "Constants/Style";
import { Prev } from "react-bootstrap/esm/PageItem";
import { fetchAddressFromPincode } from "helpers/geonamesHelper";

interface Address {
  address: string;
  pincode: string;
  city: string;
  state: string;
  country: string;
  phone_number?: string;
}

interface AddressFormModalProps {
  onSuccess: (address: Address) => void;
  onCancel: () => void;
  company_id: string;
  formik?: any;
}

const initialAddress: Address = {
  address: "",
  pincode: "",
  city: "",
  state: "",
  country: "",
  phone_number: "",
};

const baseAddressSchema = Yup.object().shape({
  address: Yup.string().required("Address is required"),
  pincode: Yup.string().required("Pin code is required"),
  country: Yup.string().required("Country is required"),
  phone_number: Yup.string()
    .required("Phone number is required")
    .test(
      "min-digits",
      "Phone number must contain at least 6 digits",
      (val) => (val?.replace(/\D/g, "") ?? "").length >= 6
    ),
});

const AddressFormModal: React.FC<AddressFormModalProps> = ({
  company_id,
  formik,
  onSuccess,
  onCancel,
}) => {
  const [currentAddress, setCurrentAddress] = useState<Address>(initialAddress);
  const [addressErrors, setAddressErrors] = useState<Partial<Address>>({});
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<any>();

  const commonInputStyle = {
    fontSize: "0.8rem",
    minHeight: 38,
    padding: "0.375rem 0.75rem",
  };

  const commonLabelStyle = {
    fontSize: "0.8rem",
    marginBottom: 6,
    marginTop: 12,
  };

  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
  }, []);

  const handleCountryChange = (selectedCountry: any) => {
    setCurrentAddress((prev) => ({
      ...prev,
      country: selectedCountry?.name || "",
      state: "",
      city: "",
    }));

    if (selectedCountry) {
      setIsLoadingStates(true);
      const newStates = State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(newStates);
      setCities([]);
      setIsLoadingStates(false);
    }
  };

  const handleStateChange = (selectedState: any) => {
    const selectedCountry = countries.find(
      (c: any) => c.name === currentAddress.country
    );

    setCurrentAddress((prev) => ({
      ...prev,
      state: selectedState?.name || "",
      city: "",
    }));

    if (selectedCountry && selectedState) {
      setIsLoadingCities(true);
      const newCities = City.getCitiesOfState(
        selectedCountry.isoCode,
        selectedState.isoCode
      );
      setCities(newCities);
      setIsLoadingCities(false);
    }
  };

  const handleCityChange = (selectedCity: any) => {
    setCurrentAddress((prev) => ({
      ...prev,
      city: selectedCity?.name || "",
    }));
  };

  const triggerPincodeLookup = async (pin: string) => {
    console.log("triggerPincodeLookup called with pin:", pin);
    const selectedCountry = countries.find(
      (c: any) => c.name === currentAddress.country
    );
    const countryCode = selectedCountry?.isoCode;

    try {
      const data = await fetchAddressFromPincode(pin, countryCode);
      if (data) {
        const resolvedCountry = countries.find(
          (c: any) => c.isoCode === data.countryCode
        ) || Country.getAllCountries().find(
          (c: any) => c.isoCode === data.countryCode
        );

        if (resolvedCountry) {
          const countryStates = State.getStatesOfCountry(resolvedCountry.isoCode);
          setStates(countryStates);

          const resolvedState = countryStates.find(
            (s: any) =>
              s.name?.toLowerCase() === data.adminName1?.toLowerCase() ||
              s.isoCode?.toLowerCase() === data.adminCode1?.toLowerCase()
          );

          let finalCities: any[] = [];
          if (resolvedState) {
            finalCities = City.getCitiesOfState(
              resolvedCountry.isoCode,
              resolvedState.isoCode
            );
            setCities(finalCities);
          }

          setCurrentAddress((prev) => ({
            ...prev,
            country: resolvedCountry.name,
            state: resolvedState ? resolvedState.name : data.adminName1 || "",
            city: data.placeName || "",
          }));
        }
      }
    } catch (err) {
      console.error("Failed to fetch address details for pincode", err);
    }
  };

  const handlePincodeBlur = async () => {
    const pin = currentAddress.pincode?.trim();
    if (pin) {
      triggerPincodeLookup(pin);
    }
  };

  const validateAddress = async (): Promise<boolean> => {
    try {
      await baseAddressSchema.validate(currentAddress, { abortEarly: false });

      const selectedCountry = countries.find(
        (c: any) => c.name === currentAddress.country
      );
      const countryStates = selectedCountry
        ? State.getStatesOfCountry(selectedCountry.isoCode)
        : [];
      const hasStates = countryStates.length > 0;
      const selectedState = currentAddress.state
        ? countryStates.find(
            (s: any) =>
              s.name?.toLowerCase() === currentAddress.state?.toLowerCase()
          )
        : null;
      const availableCities =
        selectedCountry && selectedState
          ? City.getCitiesOfState(
              selectedCountry.isoCode,
              selectedState.isoCode
            )
          : [];
      const hasCities = availableCities.length > 0;

      const errors: Partial<Address> = {};
      if (hasStates && !currentAddress.state?.trim()) {
        errors.state = "State is required";
      }
      if (hasCities && !currentAddress.city?.trim()) {
        errors.city = "City is required";
      }

      if (Object.keys(errors).length > 0) {
        setAddressErrors(errors);
        return false;
      }
      setAddressErrors({});
      return true;
    } catch (error: any) {
      const errors: Partial<Address> = {};
      error.inner?.forEach((err: any) => {
        if (err.path) {
          errors[err.path as keyof Address] = err.message;
        }
      });
      setAddressErrors(errors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent form
    setIsSubmitting(true);

    const isValid = await validateAddress();
    if (isValid) {
      const shipping_addresses = [currentAddress];
      // onSuccess(currentAddress);
      // setCurrentAddress(initialAddress);
      // setAddressErrors({});
      const response: any = await Axios.post(
        `${API_ENDPOINT.MASTERS?.CUSTOMER?.UpdateShippingAddress(company_id)}`,
        { id: company_id, shipping_addresses }
      );

      const newAddress = response.data.data.createdAddress;
      // Only update formik if it's provided
      if (formik) {
        formik.setFieldValue("company.shipping_addresses", [
          ...(formik.values.company?.shipping_addresses || []),
          newAddress,
        ]);
      }
      console.log(response.data.data);
      onSuccess(response.data);
      setCurrentAddress(initialAddress);
      setAddressErrors({});
    }

    setIsSubmitting(false);
  };

  return (
    <Form autoComplete="off" onSubmit={handleSubmit} noValidate>
      <Row className="mb-3">
        <Col lg={6}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Address <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            name="address"
            placeholder="Enter Address"
            value={currentAddress.address}
            onChange={(e) => {
              setCurrentAddress((prev) => ({
                ...prev,
                [e.target.name]: e.target.value,
              }));
            }}
            isInvalid={!!addressErrors.address}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {addressErrors.address}
          </Form.Control.Feedback>
        </Col>
        <Col lg={3}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Pin Code <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            name="pincode"
            placeholder="Enter Pin Code"
            value={currentAddress.pincode}
            onBlur={handlePincodeBlur}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              setCurrentAddress((prev) => ({
                ...prev,
                [e.target.name]: val,
              }));
              if (val.length === 6) {
                triggerPincodeLookup(val);
              }
            }}
            isInvalid={!!addressErrors.pincode}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {addressErrors.pincode}
          </Form.Control.Feedback>
        </Col>
        <Col lg={6}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Country <span className="text-danger">*</span>
          </Form.Label>
          <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.name}
            value={
              countries.find((c: any) => c.name === currentAddress.country) ||
              null
            }
            onChange={(e, selectedCountry) =>
              handleCountryChange(selectedCountry)
            }
            ListboxProps={autocompleteDropdownStyles.ListboxProps}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Country"
                size="small"
                error={!!addressErrors.country}
                helperText={addressErrors.country || ""}
              />
            )}
          />
        </Col>
        <Col md={3} xs={12} className="mb-3">
          {(() => {
            const selectedCountry = countries.find(
              (c: any) => c.name === currentAddress.country
            );
            const countryStates = selectedCountry
              ? State.getStatesOfCountry(selectedCountry.isoCode)
              : [];
            const hasStates = countryStates.length > 0;

            return (
              <>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  {hasStates ? "State" : "State / Region"}{" "}
                  {hasStates && <span className="text-danger">*</span>}
                </Form.Label>
                {hasStates ? (
                  <Autocomplete
                    options={states.filter(
                      (s) =>
                        s.countryCode ===
                        countries.find(
                          (c) => c.name === currentAddress.country
                        )?.isoCode
                    )}
                    getOptionLabel={(option) => option.name}
                    loading={isLoadingStates}
                    value={
                      states.find(
                        (s: any) => s.name === currentAddress.state
                      ) || null
                    }
                    onChange={(e, selectedState) =>
                      handleStateChange(selectedState)
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt?.name || ""
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select State"
                        size="small"
                        error={!!addressErrors.state}
                        helperText={addressErrors.state || ""}
                      />
                    )}
                    disabled={!currentAddress.country}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    name="state"
                    placeholder="Enter State / Region"
                    value={currentAddress.state}
                    onChange={(e) =>
                      setCurrentAddress((prev) => ({
                        ...prev,
                        state: e.target.value,
                        city: "",
                      }))
                    }
                    isInvalid={!!addressErrors.state}
                    style={commonInputStyle}
                    disabled={!currentAddress.country}
                  />
                )}
                {!hasStates && (
                  <Form.Control.Feedback type="invalid">
                    {addressErrors.state}
                  </Form.Control.Feedback>
                )}
              </>
            );
          })()}
        </Col>
        <Col md={3} xs={12} className="mb-3">
          {(() => {
            const selectedCountry = countries.find(
              (c: any) => c.name === currentAddress.country
            );
            const countryStates = selectedCountry
              ? State.getStatesOfCountry(selectedCountry.isoCode)
              : [];
            const selectedState = currentAddress.state
              ? countryStates.find(
                  (s: any) =>
                    s.name?.toLowerCase() ===
                    currentAddress.state?.toLowerCase()
                )
              : null;
            const availableCities =
              selectedCountry && selectedState
                ? City.getCitiesOfState(
                    selectedCountry.isoCode,
                    selectedState.isoCode
                  )
                : [];
            const hasCities = availableCities.length > 0;
            const hasStates = countryStates.length > 0;

            return (
              <>
                <Form.Label className="fw-semibold" style={commonLabelStyle}>
                  City {hasCities && <span className="text-danger">*</span>}
                </Form.Label>
                {hasCities ? (
                  <Autocomplete
                    options={cities.filter(
                      (c) =>
                        c.stateCode ===
                        states.find(
                          (s) =>
                            s.name?.toLowerCase() ===
                            currentAddress.state?.toLowerCase()
                        )?.isoCode
                    )}
                    getOptionLabel={(option) => option.name}
                    loading={isLoadingCities}
                    value={
                      cities.find(
                        (c: any) =>
                          c.name?.toLowerCase() ===
                          currentAddress.city?.toLowerCase()
                      ) || null
                    }
                    onChange={(e, selectedCity) =>
                      handleCityChange(selectedCity)
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt?.name || ""
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select City"
                        size="small"
                        error={!!addressErrors.city}
                        helperText={addressErrors.city || ""}
                      />
                    )}
                    disabled={!currentAddress.state}
                  />
                ) : (
                  <Form.Control
                    type="text"
                    name="city"
                    placeholder="Enter City"
                    value={currentAddress.city}
                    onChange={(e) =>
                      setCurrentAddress((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    isInvalid={!!addressErrors.city}
                    style={commonInputStyle}
                    disabled={
                      !currentAddress.country ||
                      (hasStates && !currentAddress.state)
                    }
                  />
                )}
                {!hasCities && (
                  <Form.Control.Feedback type="invalid">
                    {addressErrors.city}
                  </Form.Control.Feedback>
                )}
              </>
            );
          })()}
        </Col>
        <Col lg={3}>
          <Form.Label className="fw-semibold" style={commonLabelStyle}>
            Phone Number <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="number"
            name="phone_number"
            placeholder="Enter Phone Number"
            value={currentAddress.phone_number || ""}
            onChange={(e) => {
              setCurrentAddress((prev) => ({
                ...prev,
                [e.target.name]: e.target.value,
              }));
            }}
            isInvalid={!!addressErrors.phone_number}
            style={commonInputStyle}
          />
          <Form.Control.Feedback type="invalid">
            {addressErrors.phone_number}
          </Form.Control.Feedback>
        </Col>
      </Row>

      {/* Footer Buttons */}
      <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
        <CancelButton
          onClick={onCancel}
          disabled={isSubmitting}
          showConfirmation={false}
        />
        <PrimarySubmitButton
          label="Add Address"
          isLoading={isSubmitting}
          loadingLabel="Adding..."
        />
      </div>
    </Form>
  );
};

export default AddressFormModal;
